'use client';

import { useState } from 'react';
import { X, User, Mail, Lock, Phone, LogOut, Package, Calendar, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth-store';
import { useTranslation } from '@/lib/i18n';

export default function AuthModal() {
  const { user, isOpen, mode, login, register, logout, closeAuth, openAuth } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstname: '',
    lastname: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const res = login(formData.email, formData.password);
    if (!res.success) {
      setError(res.error || 'Erreur de connexion');
    } else {
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setFormData({ email: '', password: '', firstname: '', lastname: '', phone: '' }); }, 1000);
    }
  };

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const res = register({
      firstname: formData.firstname,
      lastname: formData.lastname,
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
    });
    if (!res.success) {
      setError(res.error || 'Erreur d\'inscription');
    } else {
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setFormData({ email: '', password: '', firstname: '', lastname: '', phone: '' }); }, 1000);
    }
  };

  const handleLogout = () => {
    logout();
    closeAuth();
  };

  const switchMode = (newMode: 'login' | 'register') => {
    setError('');
    setSuccess(false);
    setFormData({ email: '', password: '', firstname: '', lastname: '', phone: '' });
    openAuth(newMode);
  };

  return (
    <div
      className="fixed inset-0 z-[115] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={closeAuth}
    >
      <div
        className="relative bg-[#0a0a0f] border border-[#D4AF37]/30 max-w-md w-full my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={closeAuth}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>

        <div className="p-6 sm:p-8">
          {/* Profile mode (logged in) */}
          {user && mode === 'profile' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gold-gradient flex items-center justify-center">
                  <User size={32} className="text-[#0a0a0f]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">
                  {t('auth.welcome')}
                </div>
                <h2 className="font-display text-2xl font-bold text-[#F4E8C1]">
                  {user.firstname} {user.lastname}
                </h2>
                <p className="text-xs text-[#F4E8C1]/55 mt-1">{user.email}</p>
              </div>

              <div className="bg-[#12121a] border border-[#D4AF37]/20 p-5 mb-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55">
                    <Package size={12} />
                    {t('auth.orders')}
                  </span>
                  <span className="font-display text-sm font-semibold text-gold-gradient">
                    {user.ordersCount}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-[#D4AF37]/12 pt-3">
                  <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55">
                    <Calendar size={12} />
                    {t('auth.member_since')}
                  </span>
                  <span className="text-xs text-[#F4E8C1]">
                    {new Date(user.createdAt).toLocaleDateString('fr-BE', { year: 'numeric', month: 'long' })}
                  </span>
                </div>
                {user.phone && (
                  <div className="flex items-center justify-between border-t border-[#D4AF37]/12 pt-3">
                    <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55">
                      <Phone size={12} />
                      {t('auth.phone')}
                    </span>
                    <span className="text-xs text-[#F4E8C1]">{user.phone}</span>
                  </div>
                )}
              </div>

              <div className="text-center text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/45 mb-5">
                Client Cercle MATAR · Anvers
              </div>

              <button
                onClick={handleLogout}
                className="w-full inline-flex items-center justify-center gap-2 py-3 border border-[#CE1126]/40 text-[#CE1126] hover:bg-[#CE1126] hover:text-white transition-all text-xs uppercase tracking-[0.2em] font-semibold"
              >
                <LogOut size={14} />
                {t('auth.logout')}
              </button>
            </div>
          )}

          {/* Login mode */}
          {!user && mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border border-[#D4AF37]/40 flex items-center justify-center">
                  <User size={24} className="text-[#D4AF37]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">
                  {t('auth.account')}
                </div>
                <h2 className="font-display text-2xl font-bold text-[#F4E8C1]">
                  {t('auth.login')}
                </h2>
              </div>

              {error && (
                <div className="mb-4 p-3 border border-[#CE1126]/40 bg-[#CE1126]/10 flex items-start gap-2">
                  <AlertCircle size={14} className="text-[#CE1126] flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-[#F4E8C1]">{error}</span>
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center gap-2">
                  <Check size={14} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-xs text-[#F4E8C1]">Connexion réussie</span>
                </div>
              )}

              <div className="space-y-4">
                <FormField
                  label={t('auth.email')}
                  icon={Mail}
                  type="email"
                  value={formData.email}
                  onChange={(v) => setFormData({ ...formData, email: v })}
                  required
                />
                <FormField
                  label={t('auth.password')}
                  icon={Lock}
                  type="password"
                  value={formData.password}
                  onChange={(v) => setFormData({ ...formData, password: v })}
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full mt-6 py-3.5 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.2em] text-xs hover:glow-gold transition-all"
              >
                {t('auth.login.cta')}
              </button>

              <p className="text-center text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 mt-5">
                {t('auth.no_account')}{' '}
                <button
                  type="button"
                  onClick={() => switchMode('register')}
                  className="text-[#D4AF37] hover:underline font-semibold"
                >
                  {t('auth.create_one')}
                </button>
              </p>
            </form>
          )}

          {/* Register mode */}
          {!user && mode === 'register' && (
            <form onSubmit={handleRegister}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border border-[#D4AF37]/40 flex items-center justify-center">
                  <User size={24} className="text-[#D4AF37]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">
                  {t('auth.account')}
                </div>
                <h2 className="font-display text-2xl font-bold text-[#F4E8C1]">
                  {t('auth.register')}
                </h2>
              </div>

              {error && (
                <div className="mb-4 p-3 border border-[#CE1126]/40 bg-[#CE1126]/10 flex items-start gap-2">
                  <AlertCircle size={14} className="text-[#CE1126] flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-[#F4E8C1]">{error}</span>
                </div>
              )}

              {success && (
                <div className="mb-4 p-3 border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center gap-2">
                  <Check size={14} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-xs text-[#F4E8C1]">Compte créé</span>
                </div>
              )}

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    label={t('auth.firstname')}
                    icon={User}
                    value={formData.firstname}
                    onChange={(v) => setFormData({ ...formData, firstname: v })}
                    required
                  />
                  <FormField
                    label={t('auth.lastname')}
                    icon={User}
                    value={formData.lastname}
                    onChange={(v) => setFormData({ ...formData, lastname: v })}
                    required
                  />
                </div>
                <FormField
                  label={t('auth.email')}
                  icon={Mail}
                  type="email"
                  value={formData.email}
                  onChange={(v) => setFormData({ ...formData, email: v })}
                  required
                />
                <FormField
                  label={t('auth.phone')}
                  icon={Phone}
                  type="tel"
                  value={formData.phone}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                />
                <FormField
                  label={t('auth.password')}
                  icon={Lock}
                  type="password"
                  value={formData.password}
                  onChange={(v) => setFormData({ ...formData, password: v })}
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full mt-6 py-3.5 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.2em] text-xs hover:glow-gold transition-all"
              >
                {t('auth.register.cta')}
              </button>

              <p className="text-center text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 mt-5">
                {t('auth.has_account')}{' '}
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="text-[#D4AF37] hover:underline font-semibold"
                >
                  {t('auth.login_instead')}
                </button>
              </p>
            </form>
          )}

          {/* If logged in but mode is login or register, show profile */}
          {user && (mode === 'login' || mode === 'register') && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold-gradient flex items-center justify-center">
                <Check size={28} className="text-[#0a0a0f]" />
              </div>
              <p className="font-serif italic text-lg text-[#F4E8C1]/85 mb-2">
                {t('auth.welcome')}, {user.firstname} !
              </p>
              <p className="text-xs text-[#F4E8C1]/55 mb-6">Vous êtes connecté.</p>
              <button
                onClick={() => openAuth('profile')}
                className="px-7 py-3 bg-gold-gradient text-[#0a0a0f] font-semibold uppercase tracking-[0.18em] text-xs hover:glow-gold transition-all"
              >
                {t('auth.profile')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  icon: Icon,
  type = 'text',
  value,
  onChange,
  required,
}: {
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">
        {label}
      </label>
      <div className="relative">
        <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]/60" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="w-full bg-[#0a0a0f] border border-[#D4AF37]/25 pl-9 pr-4 py-3 text-sm text-[#F4E8C1] placeholder:text-[#F4E8C1]/30 focus:border-[#D4AF37] focus:outline-none transition-colors"
        />
      </div>
    </div>
  );
}
